import React, { useState, useEffect, useRef } from 'react';
import { useIsMobile } from '../../hooks/useResponsive';
import { useAuth } from '../../context/AuthContext';
import {
  Settings as SettingsIcon,
  User,
  Shield,
  Link,
  Mail,
  Menu,
  ChevronDown
} from 'lucide-react';
import { getUserProfile, updateUserProfile } from '../../firebase/user.service';
import { auth, storage } from '../../firebase/firebase.config';
import { updateProfile } from 'firebase/auth';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import useAppStore from '../../store/appStore';
import { useUrlState } from '../../hooks/useUrlState';
import { resolveStoredPhotoUrl } from '../../utils/avatar';

// Section components
import ProfileSection from './sections/ProfileSection';
import SecuritySection from './sections/SecuritySection';
import IntegrationsSection from './sections/IntegrationsSection';
import InboxSection from './sections/InboxSection';

const Settings = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useUrlState('section', 'profile');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);

  // Zustand store selectors
  const settingsProfile = useAppStore((s) => s.settingsProfile);
  const setSettingsProfile = useAppStore((s) => s.setSettingsProfile);
  const setSettingsEmailSignature = useAppStore((s) => s.setSettingsEmailSignature);

  // Profile state
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [license, setLicense] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [photoFile, setPhotoFile] = useState(null);

  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Populate form fields once on initial load from Zustand store or Firestore.
  // Uses a ref guard so subsequent store updates never overwrite unsaved edits.
  const formInitialized = useRef(false);

  const populateForm = (profile) => {
    setFullName(profile.fullName || profile.displayName || '');
    setPhone(profile.phone || '');
    setTitle(profile.role || profile.title || '');
    setLicense(profile.license || '');
    setCompanyName(profile.companyName || profile.brokerage || '');
    setPhotoURL(resolveStoredPhotoUrl(profile, user || auth.currentUser));
  };

  useEffect(() => {
    if (formInitialized.current) return;

    if (settingsProfile) {
      populateForm(settingsProfile);
      formInitialized.current = true;
      setLoadingProfile(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (userId) {
          const profile = await getUserProfile(userId);
          if (profile) {
            populateForm(profile);
            formInitialized.current = true;
            setSettingsProfile(profile);
            if (profile.emailSignature) {
              setSettingsEmailSignature(profile.emailSignature);
            }
          } else {
            setPhotoURL(resolveStoredPhotoUrl(null, user || auth.currentUser));
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [settingsProfile]); // eslint-disable-line

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      let currentPhotoURL = photoURL || resolveStoredPhotoUrl(settingsProfile, user || auth.currentUser);

      if (photoFile) {
        const storageRef = ref(storage, `profilePhotos/${userId}`);
        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, photoFile);
          task.on('state_changed', null, reject, async () => {
            currentPhotoURL = await getDownloadURL(task.snapshot.ref);
            resolve();
          });
        });
        setPhotoURL(currentPhotoURL);
        setPhotoFile(null);
      }

      await updateUserProfile(userId, {
        fullName,
        phone,
        role: title,
        license,
        companyName,
        photoURL: currentPhotoURL,
      });

      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: fullName,
          photoURL: currentPhotoURL,
        });
      }

      // Write-through: update Zustand settings cache so other views see fresh data
      const updatedProfile = {
        ...(settingsProfile || {}),
        fullName,
        phone,
        role: title,
        license,
        companyName,
        photoURL: currentPhotoURL,
      };
      setSettingsProfile(updatedProfile);

      alert('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const navItems = [
    { id: 'profile', label: 'Profile & Branding', icon: User, group: 'account' },
    { id: 'security', label: 'Security', icon: Shield, group: 'account' },
    { id: 'integrations', label: 'Integrations', icon: Link, group: 'system' },
    { id: 'inbox', label: 'Email Signature', icon: Mail, group: 'system' },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <ProfileSection
            isMobile={isMobile}
            fullName={fullName}
            setFullName={setFullName}
            phone={phone}
            setPhone={setPhone}
            title={title}
            setTitle={setTitle}
            license={license}
            setLicense={setLicense}
            companyName={companyName}
            setCompanyName={setCompanyName}
            photoURL={photoURL}
            onPhotoChange={(file) => setPhotoFile(file)}
            onSave={handleSaveProfile}
            saving={savingProfile}
          />
        );

      case 'security':
        return (
          <SecuritySection
            isMobile={isMobile}
            currentPassword={currentPassword}
            setCurrentPassword={setCurrentPassword}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
          />
        );

      case 'integrations':
        return (
          <IntegrationsSection isMobile={isMobile} />
        );

      case 'inbox':
        return (
          <InboxSection isMobile={isMobile} />
        );

      default:
        return null;
    }
  };

  if (isMobile) {
    return (
      <div className="flex flex-col h-full bg-white">
        <header className="flex-shrink-0 bg-white/95 backdrop-blur-md border-b border-notion-border z-50 sticky top-0">
          <div className="px-4 pt-3 pb-2 flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-stone-600" />
            <span className="font-semibold text-base tracking-tight text-notion-text">Settings</span>
          </div>

          <div className="px-4 pb-3 w-full">
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Menu className="w-4 h-4 text-stone-500" />
              </div>
              <select
                value={activeSection}
                onChange={(e) => setActiveSection(e.target.value)}
                className="w-full bg-notion-sidebar hover:bg-notion-hover border border-transparent focus:border-stone-300 rounded-lg py-2 pl-10 pr-10 text-sm font-medium text-notion-text appearance-none outline-none transition-colors cursor-pointer"
              >
                {navItems.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <ChevronDown className="w-4 h-4 text-stone-400" />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto no-scrollbar bg-stone-50 pb-24">
          <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
            {renderSection()}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white relative overflow-hidden">
      <header className="flex-shrink-0 border-b border-notion-border px-8 py-5 bg-white z-20">
        <h1 className="text-xl font-semibold text-notion-text tracking-tight">Settings</h1>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-64 bg-white border-r border-notion-border overflow-y-auto p-4 flex-shrink-0">
          <div className="space-y-1">
            <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2 px-3 mt-2">Account</div>
            {navItems.filter(item => item.group === 'account').map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-[13px] cursor-pointer transition-colors ${activeSection === item.id
                    ? 'bg-notion-hover font-semibold text-notion-text'
                    : 'text-notion-text hover:bg-notion-hover'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </div>
              );
            })}

            <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2 px-3 mt-6">System</div>
            {navItems.filter(item => item.group === 'system').map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-[13px] cursor-pointer transition-colors ${activeSection === item.id
                    ? 'bg-notion-hover font-semibold text-notion-text'
                    : 'text-notion-text hover:bg-notion-hover'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-stone-50/50 p-10 relative">
          <div className="max-w-3xl mx-auto space-y-8 pb-32">
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
